import React from 'react';
import cn from 'classnames';

import type { TabId } from '../types';

export interface TabDescriptor {
  id: TabId;
  label: string;
  icon: string;
  hasError?: boolean;
}

interface TabNavProps {
  tabs: TabDescriptor[];
  activeTab: TabId;
  onChange: (id: TabId) => void;
}

export const TabNav: React.FC<TabNavProps> = ({
  tabs,
  activeTab,
  onChange,
}) => (
  <div
    role="tablist"
    aria-label="Dossier sections"
    className="flex w-full overflow-x-auto rounded-xl border border-slate-200 bg-slate-50 p-1 dark:border-gray-700 dark:bg-gray-900/40"
  >
    {tabs.map((tab, idx) => {
      const active = activeTab === tab.id;
      return (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(tab.id)}
          className={cn(
            'relative flex flex-1 min-w-[140px] items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-all',
            active
              ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-800 dark:text-blue-400'
              : 'text-title-page-color-300 hover:text-title-page-color-100 dark:text-gray-400 dark:hover:text-gray-200'
          )}
        >
          <span
            className={cn(
              'flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold tabular-nums',
              active
                ? 'bg-blue-500 text-white'
                : 'bg-slate-200 text-slate-600 dark:bg-gray-700 dark:text-gray-300'
            )}
          >
            {idx + 1}
          </span>
          <span className="hidden md:inline">{tab.label}</span>
          <span aria-hidden className="md:hidden">
            {tab.icon}
          </span>
          {tab.hasError && (
            <span
              aria-hidden
              className="absolute right-2 top-1.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white dark:ring-gray-800"
            />
          )}
        </button>
      );
    })}
  </div>
);
